/* entrepreneurshipNormalizer.js
   Maps the NEW Entrepreneurship_Pathway_Master_AP_Telangana.json shape into the
   flat-array shape the existing OwnPath UI (EntrepreneurshipPage.jsx,
   SchemeDetail.jsx, EntrepreneurshipDetail.jsx) was originally built around.
   Keeps the original visual chrome intact; only the data plumbing changes. */

const REGION_EMOJI = {
  'Andhra Pradesh': '🟡',
  'Telangana': '🟢',
  'Central': '🇮🇳',
  'India': '🇮🇳',
};

const CATEGORY_EMOJI = {
  'Food & Beverage': '🍹',
  'Food': '🍔',
  'Food Processing': '🌾',
  'Green Energy': '☀️',
  'EV/Mobility': '⚡',
  'Digital': '💻',
  'Services': '🛠️',
  'Repair/Retail': '🔧',
  'Manufacturing': '🏭',
  'Education': '📚',
  'Fashion & Tailoring': '✂️',
  'Beauty & Wellness': '💆',
  'Health': '🩺',
  'Agriculture': '🌱',
  'Logistics': '🚚',
  'Retail': '🏪',
  'Crafts': '🎨',
};

const emojiFor = (cat) => CATEGORY_EMOJI[cat] || '💡';
const emojiForRegion = (r) => REGION_EMOJI[r] || '🏛️';

/* ── Normalize a single scheme — already mostly matches old UI; just add emoji */
const normalizeScheme = (s) => ({
  ...s,
  emoji: s.emoji || emojiForRegion(s.region),
  // Old UI expects officialGovernmentLinks[].label & .url — new file already has this
  // Old UI shows applyProcess as steps — keep as-is
});

/* ── Normalize a business idea → look like old "pathway" object ── */
const normalizeIdea = (idea) => {
  const detail = idea.detail || {};

  // Map detail.starterCosts (array of [label, amount] tuples) into old shape
  const starterCosts = (detail.starterCosts || []).map((row) =>
    Array.isArray(row) ? { item: row[0], amount: row[1] } : row,
  );

  // selfCheckQuiz tuples → { question, yesMeaning, noMeaning } (matches PathwayQuiz expectations)
  const selfCheckQuiz = (detail.selfCheckQuiz || []).map((row) =>
    Array.isArray(row) ? { question: row[0], yesMeaning: row[1], noMeaning: row[2] } : row,
  );

  const myths = (detail.myths || []).map((row) =>
    Array.isArray(row) ? { myth: row[0], reality: row[1] } : row,
  );

  return {
    ...idea,
    emoji: emojiFor(idea.category),
    bestFor: idea.whoShouldChoose || [],
    previewCard: {
      subtext: idea.demandLogic,
      chips: idea.audienceTags || [],
    },
    /* Replicate the old "sections" tree so EntrepreneurshipDetail.jsx renders */
    sections: {
      snapshot: {
        whatItIs: idea.demandLogic,
        startingBudget: idea.startBudget,
        timeToTest: detail.timeToTest || '3-7 days',
        earningLogic: idea.marginLogic,
      },
      overview: {
        whyItWorks: idea.demandLogic,
        customerSegments: idea.whoShouldChoose || [],
        pricingModel: detail.pricing,
      },
      selfCheckQuiz,
      whoShouldChoose: idea.whoShouldChoose || [],
      costing: {
        starterCosts,
        monthlyOrDailyCosts: [],
        simpleFormula: idea.marginLogic,
        examplePricing: detail.pricing,
        profitReality: idea.profitReality || null,
      },
      process: idea.firstSteps || [],
      myths,
      governmentSupport: idea.schemesToUse || [],
    },
  };
};

/* ── Main normalize: produce a package that has BOTH new shape (for new
   sections that need it) AND old shape (schemes[] + pathways[] flat arrays
   that the existing UI binds to). ── */
export const normalizeEntrepreneurship = (raw) => {
  if (!raw) return null;

  const schemesRaw = raw.schemes || {};
  const schemes = [
    ...(schemesRaw.andhraPradesh || []).map(normalizeScheme),
    ...(schemesRaw.telangana    || []).map(normalizeScheme),
    ...(schemesRaw.central      || []).map(normalizeScheme),
  ];

  const pathways = (raw.businessIdeas?.ideas || []).map(normalizeIdea);

  return {
    ...raw,
    // Flat arrays the existing UI components expect
    schemes,
    pathways,
    // Surface meta.disclaimer at metadata.disclaimer for legacy UI
    metadata: {
      ...(raw.metadata || {}),
      disclaimer: raw.meta?.disclaimer || raw.metadata?.disclaimer,
      title: raw.meta?.title,
      version: raw.meta?.version,
    },
    // Surface budget bands at top level for easier UI access
    budgetBands: raw.businessIdeas?.budgetBands || [],
  };
};

/* Find by id — id can match either schemes or pathways */
export const findSchemeById = (pkg, id) =>
  pkg?.schemes?.find((s) => s.id === id) || null;

export const findPathwayById = (pkg, id) =>
  pkg?.pathways?.find((p) => p.id === id) || null;
